/* ============================================================
   Kit builder — recipe engine (position maps)
   ------------------------------------------------------------
   Pure logic: data in, colour sets out. No DOM, no Shopify
   calls. Runs in node for testing; drops into theme JS as-is.

   2026-10-22 (Clew, with Lisa): the relationship-graph walker is
   gone. Recipes are POSITION MAPS only: "Z0;Z2;Z4" means slot 1
   takes the colourway's colour at position 0, slot 2 position 2,
   slot 3 position 4. Colourways are ordered light → dark, so one
   map applied across several colourways gives several sets.
   Any other token ("A-", "(w)B-+", ...) is an old walker recipe
   and throws "needs converting" — the generator logs and skips it.
   The walker lives in git history (pre-kit-simplify) if wanted.

   INPUTS:
   colourway: { title, swatch: [ { name, hex, stock }, ... ] }
              (swatch order = Z0, Z1, Z2, ...; light → dark)
   recipe:    "Z0;Z2;Z4"   (semicolon-delimited, one token per slot)
              "Z0;Z2;=Z2;Z4"  "=" marks a slot that shares a colour
              on purpose (S6)
   unitsPerSlot: [5, 5, 5]  units of stock each slot needs
   opts.slotOk(colourway, entry, slotIdx, slots) → bool   (optional)
              extra qualification per slot, e.g. "its buddy strand
              can be covered too". Unqualified = unavailable.
              `slots` lists every slot that would take this colour:
              just [slotIdx] unless slots share it (S7).
   opts.groupUnits(slots) → units   (optional)
              stock that slots sharing one colour need together;
              default, their units added. The generator pools their
              metres and rounds up once instead (G12).

   OUTPUT of resolveHardReference(): null, or
   { assignment: { Z: title }, colours: [ { family, slotRef, index,
     name, hex, stock, offsetFrom? }, ... ], score: null, warnings: [] }
     offsetFrom is present only when a slot was shifted off its
     mapped position (it holds the mapped position).

   SHIFTING RULE (Lisa, 2026-10-22):
   S1. A position is available for a slot if the colourway has an
       entry there, its stock covers that slot's units, and slotOk
       (if given) passes.
   S2. Every slot gets a distinct available position from THIS
       colourway — never a colour from outside it. (Slots marked
       to share, S6, count as one.)
   S3. Order is kept: if the map puts slot X lighter than slot Y,
       the result does too.
   S4. Among all assignments meeting S1–S3, pick by:
         a. smallest total shift from the mapped positions;
         b. then most evenly spread — largest smallest gap between
            neighbouring picks, then least variance in those gaps;
         c. then lowest combined stock (sell down the thin ones);
         d. then lighter (lexicographically lower positions).
       If every mapped position is available, nothing shifts: the
       result is exactly the old all-or-nothing result.
   S5. No valid assignment (fewer usable positions than slots) →
       null: the colourway is skipped for this yarn/size.
   S6. (Lisa, 2026-10-22) A position may appear twice only on
       purpose, and the repeat says so with "=": Z0;Z2;=Z2;Z4 gives
       slots 2 and 3 one colour (a design for 5 colours kitted
       with 4). An unmarked repeat (Z0;Z2;Z2;Z4) is taken for a
       typo: parseRecipe throws with the marked version in the
       message; the generator logs it once and skips the map.
       Every extra use needs its own "=" (Z2;=Z2;=Z2). A lone =Z2
       with nothing to share reads as Z2.
   S7. Slots sharing a position move as one: one colour for all
       of them, stock for all of them together (opts.groupUnits,
       default their units added), slotOk asked once
       with every member slot, and if that colour falls short they
       all slide to the same replacement. Shift (S4a) counts per
       slot, so moving a shared colour costs once for each slot it
       fills; spread and stock (S4b, c) count each colour once.
   ============================================================ */

'use strict';

/* ---------- recipe parsing ---------- */

function parseRecipe(str) {
  const tokens = String(str)
    .split(';')
    .map(t => t.trim())
    .filter(Boolean)
    .map(raw => {
      const hard = raw.match(/^(=)?\s*Z(\d+)$/i);
      if (hard) return { type: 'hard', index: parseInt(hard[2], 10), shared: !!hard[1], raw };
      const late = raw.match(/^Z(\d+)\s*=$/i);
      if (late) throw new Error(`recipe token "${raw}" — the share mark goes in front: =Z${late[1]}`);
      throw new Error(`walker-style recipe token "${raw}" — needs converting to a position map (Z0;Z2;...)`);
    });
  checkRepeats(tokens, str);                                  // S6
  return tokens;
}

// S6: a position may be used unmarked once; every extra use needs "=".
// An unmarked repeat is taken for a typo, and the error carries the fix.
function checkRepeats(tokens, str) {
  const plain = new Map();                                    // position → unmarked uses
  for (const t of tokens) if (!t.shared) plain.set(t.index, (plain.get(t.index) || 0) + 1);
  const repeated = new Set([...plain].filter(([, uses]) => uses > 1).map(([p]) => p));
  if (!repeated.size) return;
  const first = new Set();
  const marked = tokens.map(t => {
    if (t.shared || !repeated.has(t.index)) return t.raw;
    if (!first.has(t.index)) { first.add(t.index); return t.raw; }
    return '=Z' + t.index;
  }).join(';');
  throw new Error(`position map "${String(str).trim()}" repeats a position (` +
    [...repeated].map(p => 'Z' + p).join(', ') +
    `) — a typo, or if the slots share one colour on purpose, mark the repeat: "${marked}"`);
}

/* ---------- shifting search ---------- */

function gapStats(sortedPositions) {
  if (sortedPositions.length < 2) return { minGap: Infinity, variance: 0 };
  const gaps = [];
  for (let i = 1; i < sortedPositions.length; i++) gaps.push(sortedPositions[i] - sortedPositions[i - 1]);
  const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const variance = gaps.reduce((a, g) => a + (g - mean) * (g - mean), 0) / gaps.length;
  return { minGap: Math.min(...gaps), variance };
}

// a better than b? (S4 a–d)
function better(a, b) {
  if (!b) return true;
  if (a.shift !== b.shift) return a.shift < b.shift;
  if (a.minGap !== b.minGap) return a.minGap > b.minGap;
  if (Math.abs(a.variance - b.variance) > 1e-9) return a.variance < b.variance;
  if (a.stock !== b.stock) return a.stock < b.stock;
  for (let i = 0; i < a.sorted.length; i++) {
    if (a.sorted[i] !== b.sorted[i]) return a.sorted[i] < b.sorted[i];
  }
  return false;
}

// returns positions per slot (slot order), or null. `shared[slot]` is
// true where the map marked that slot with "=" (S6); groupUnits(slots),
// if given, says what stock slots sharing a colour need together (S7).
function assignPositions(colourway, mapped, unitsPerSlot, slotOk, shared, groupUnits) {
  const n = colourway.swatch.length;
  const k = mapped.length;
  if (k === 0) return null;
  // the same S6 check parseRecipe makes, for callers who skip it
  const spelled = mapped.map((p, slot) => (shared && shared[slot] ? '=Z' : 'Z') + p);
  checkRepeats(spelled.map((raw, slot) => ({ index: mapped[slot], shared: raw[0] === '=', raw })), spelled.join(';'));

  // one group per distinct mapped position, light → dark (S3); slots
  // marked to share a position are one group (S7)
  const bySpot = new Map();
  mapped.forEach((p, slot) => bySpot.set(p, [...(bySpot.get(p) || []), slot]));
  const groups = [...bySpot.entries()].sort((a, b) => a[0] - b[0]).map(([p, slots]) => ({
    p, slots,
    units: slots.length > 1 && groupUnits
      ? groupUnits(slots)
      : slots.reduce((sum, slot) => sum + (unitsPerSlot[slot] ?? 1), 0)
  }));
  const g = groups.length;

  const ok = (grp, pos) => {
    const c = colourway.swatch[pos];
    if (!c) return false;
    if (c.stock < grp.units) return false;
    if (slotOk && !slotOk(colourway, c, grp.slots[0], grp.slots)) return false;
    return true;
  };

  let best = null;
  const picks = new Array(g);

  // positions strictly increase from group to group, which enforces
  // both S2 (distinct) and S3 (order kept)
  (function walk(i, from, shift, stock) {
    if (best && shift > best.shift) return;               // can't win on (a)
    if (i === g) {
      const sorted = picks.slice();
      const { minGap, variance } = gapStats(sorted);
      const cand = { shift, minGap, variance, stock, sorted };
      if (better(cand, best)) best = cand;
      return;
    }
    const grp = groups[i];
    // leave room for the groups still to place
    for (let pos = from; pos <= n - (g - i); pos++) {
      if (!ok(grp, pos)) continue;
      picks[i] = pos;
      walk(i + 1, pos + 1, shift + Math.abs(pos - grp.p) * grp.slots.length, stock + colourway.swatch[pos].stock);
    }
  })(0, 0, 0, 0);

  if (!best) return null;
  const perSlot = new Array(k);
  groups.forEach((grp, i) => grp.slots.forEach(slot => { perSlot[slot] = best.sorted[i]; }));
  return perSlot;
}

/* ---------- top-level ---------- */

function resolveHardReference(recipeStr, colourway, unitsPerSlot, opts) {
  const tokens = parseRecipe(recipeStr);
  const mapped = tokens.map(t => t.index);
  const shared = tokens.map(t => t.shared);
  const slotOk = opts && typeof opts.slotOk === 'function' ? opts.slotOk : null;
  const groupUnits = opts && typeof opts.groupUnits === 'function' ? opts.groupUnits : null;
  const positions = assignPositions(colourway, mapped, unitsPerSlot || [], slotOk, shared, groupUnits);
  if (!positions) return null;                              // S5

  const colours = positions.map((pos, slot) => {
    const c = colourway.swatch[pos];
    const out = {
      family: colourway.title,
      slotRef: `Z${mapped[slot]}`,
      index: pos,
      name: c.name,
      hex: c.hex,
      stock: c.stock
    };
    if (pos !== mapped[slot]) out.offsetFrom = mapped[slot];
    return out;
  });
  return { assignment: { Z: colourway.title }, colours, score: null, warnings: [] };
}

/* ---------- exports ---------- */
// node (test harness) and browser (window.RecipeEngine) alike.

const RecipeEngine = { parseRecipe, resolveHardReference, assignPositions };

if (typeof module !== 'undefined' && module.exports) module.exports = RecipeEngine;
if (typeof window !== 'undefined') window.RecipeEngine = RecipeEngine;